import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CalendarDays,
  Download,
  ClipboardList,
  IndianRupee,
  CheckCircle2,
  Wallet,
  PackageOpen,
  RefreshCcw,
} from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { inr, StatusBadge } from "@/lib/utils";

export default function AdminReports() {
  const { t } = useLang();

  const today = new Date().toLocaleDateString("en-CA", {
    timeZone: "Asia/Kolkata",
  });

  const [mode, setMode] = useState("daily");
  const [date, setDate] = useState(today);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [year, setYear] = useState(today.slice(0, 4));
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [data, setData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [busy, setBusy] = useState(false);

  const getPeriod = () => {
    if (mode === "daily") {
      return { from: date, to: date };
    }

    if (mode === "monthly") {
      const [y, m] = month.split("-").map(Number);
      const lastDay = new Date(y, m, 0).getDate();
      return {
        from: `${month}-01`,
        to: `${month}-${String(lastDay).padStart(2, "0")}`,
      };
    }

    if (mode === "yearly") {
      return {
        from: `${year}-01-01`,
        to: `${year}-12-31`,
      };
    }

    return {
      from: fromDate,
      to: toDate,
    };
  };

  const loadPeriod = async () => {
    setBusy(true);
    try {
      const { from, to } = getPeriod();
      const res = await api.get("/admin/reports/period", {
        params: {
          from_date: from,
          to_date: to,
        },
      });
      setData(res.data);
    } catch (e) {
      toast.error(fmtErr(e));
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  const loadDaily = async () => {
    if (mode !== "daily") return;

    try {
      const res = await api.get("/admin/reports/daily", {
        params: { date },
      });
      setDailyData(res.data);
    } catch (e) {
      toast.error(fmtErr(e));
      setDailyData(null);
    }
  };

  useEffect(() => {
    loadPeriod();
  }, [mode, date, month, year, fromDate, toDate]);

  useEffect(() => {
    loadDaily();
  }, [mode, date]);

  const downloadExcel = async () => {
    setBusy(true);

    try {
      const { from, to } = getPeriod();

      const res = await api.get("/admin/reports/daily/export", {
        params: { date: from },
        responseType: "blob",
      });

      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sona-Report-${from}-to-${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success("Report downloaded");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const period = getPeriod();
  const totals = data?.totals || {};

  const cards = [
    {
      label: "Opening Stock",
      value: `${Number(totals.opening_mt || 0).toFixed(3)} MT`,
      icon: PackageOpen,
    },
    {
      label: "Stock Received",
      value: `${Number(totals.received_mt || 0).toFixed(3)} MT`,
      icon: Download,
    },
    {
      label: "Sales",
      value: `${Number(totals.sale_mt || 0).toFixed(3)} MT`,
      icon: ClipboardList,
    },
    {
      label: "Closing Stock",
      value: `${Number(totals.closing_mt || 0).toFixed(3)} MT`,
      icon: CheckCircle2,
    },
  ];

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">
            Reports & Stock Statements
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Daily, monthly, yearly and custom-period business and stock reports.
          </p>
        </div>

        <button
          className="sf-btn-outline"
          onClick={loadPeriod}
          disabled={busy}
        >
          <RefreshCcw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="sf-card p-4">
        <div className="flex flex-wrap gap-2">
          {[
            ["daily", "Daily"],
            ["monthly", "Monthly"],
            ["yearly", "Yearly"],
            ["custom", "Custom Period"],
          ].map(([value, label]) => (
            <button
              key={value}
              onClick={() => setMode(value)}
              className={
                mode === value
                  ? "sf-btn-primary"
                  : "sf-btn-outline"
              }
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          {mode === "daily" && (
            <div>
              <label className="sf-label">Date</label>
              <div className="relative">
                <CalendarDays className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="date"
                  className="sf-input pl-9"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
            </div>
          )}

          {mode === "monthly" && (
            <div>
              <label className="sf-label">Month</label>
              <input
                type="month"
                className="sf-input"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
            </div>
          )}

          {mode === "yearly" && (
            <div>
              <label className="sf-label">Year</label>
              <input
                type="number"
                className="sf-input"
                min="2000"
                max="2100"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </div>
          )}

          {mode === "custom" && (
            <>
              <div>
                <label className="sf-label">From</label>
                <input
                  type="date"
                  className="sf-input"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div>
                <label className="sf-label">To</label>
                <input
                  type="date"
                  className="sf-input"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </>
          )}

          <button
            className="sf-btn-primary"
            onClick={loadPeriod}
            disabled={busy}
          >
            {busy ? "Preparing..." : "Generate Report"}
          </button>

          <button
            className="sf-btn-outline"
            onClick={downloadExcel}
            disabled={busy}
          >
            <Download className="h-4 w-4" />
            Excel
          </button>
        </div>
      </div>

      <div className="sf-card p-5">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
          Selected Period
        </div>
        <div className="font-display text-xl font-extrabold text-slate-900 mt-1">
          {period.from} → {period.to}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="sf-card p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {c.label}
              </span>
              <c.icon className="h-4 w-4 text-slate-400" />
            </div>
            <div className="font-display text-2xl font-extrabold text-slate-900 mt-2">
              {c.value}
            </div>
          </div>
        ))}
      </div>

      <div className="sf-card overflow-x-auto">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-display font-bold text-slate-900">
            Stock Statement
          </h2>
        </div>

        <table className="w-full min-w-[1100px]">
          <thead>
            <tr>
              <th className="sf-th">Sl.</th>
              <th className="sf-th">Company</th>
              <th className="sf-th">Product / Grade</th>
              <th className="sf-th">Opening (MT)</th>
              <th className="sf-th">Received (MT)</th>
              <th className="sf-th">Total (MT)</th>
              <th className="sf-th">Sale (MT)</th>
              <th className="sf-th">Return (MT)</th>
              <th className="sf-th">Closing (MT)</th>
            </tr>
          </thead>

          <tbody>
            {(data?.rows || []).map((r, i) => (
              <tr key={r.product_id || i} className="sf-tr">
                <td className="sf-td">{i + 1}</td>
                <td className="sf-td">{r.company_name}</td>
                <td className="sf-td font-semibold text-slate-900">
                  {r.product_name}
                </td>
                <td className="sf-td">{Number(r.opening_mt).toFixed(3)}</td>
                <td className="sf-td">{Number(r.received_mt).toFixed(3)}</td>
                <td className="sf-td font-semibold">{Number(r.total_mt).toFixed(3)}</td>
                <td className="sf-td">{Number(r.sale_mt).toFixed(3)}</td>
                <td className="sf-td">{Number(r.return_mt).toFixed(3)}</td>
                <td className="sf-td font-bold text-slate-900">
                  {Number(r.closing_mt).toFixed(3)}
                </td>
              </tr>
            ))}

            {(data?.rows || []).length === 0 && (
              <tr>
                <td className="sf-td text-slate-400" colSpan={9}>
                  No stock movement found for this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {mode === "daily" && dailyData && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                label: t("ordersPlaced"),
                value: dailyData.orders_count,
                icon: ClipboardList,
              },
              {
                label: t("salesValue"),
                value: inr(dailyData.sales_value),
                icon: IndianRupee,
              },
              {
                label: t("paymentsCollectedTitle"),
                value: inr(dailyData.collected),
                icon: Wallet,
              },
              {
                label: t("confirmedLabel"),
                value: dailyData.confirmed,
                icon: CheckCircle2,
              },
            ].map((c) => (
              <div key={c.label} className="sf-card p-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {c.label}
                  </span>
                  <c.icon className="h-4 w-4 text-slate-400" />
                </div>
                <div className="font-display text-2xl font-extrabold text-slate-900 mt-2">
                  {c.value}
                </div>
              </div>
            ))}
          </div>

          <div className="sf-card overflow-x-auto">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="font-display font-bold text-slate-900">
                Orders Placed on {date}
              </h2>
            </div>

            <table className="w-full min-w-[720px]">
              <thead>
                <tr>
                  <th className="sf-th">Order No.</th>
                  <th className="sf-th">Customer</th>
                  <th className="sf-th">Products</th>
                  <th className="sf-th">Amount</th>
                  <th className="sf-th">Time</th>
                  <th className="sf-th">Status</th>
                </tr>
              </thead>

              <tbody>
                {(dailyData.orders || []).map((o, i) => (
                  <tr key={i} className="sf-tr">
                    <td className="sf-td font-mono-sf font-semibold text-slate-900">
                      {o["Order No"]}
                    </td>
                    <td className="sf-td">{o.Customer}</td>
                    <td className="sf-td">{o.Items}</td>
                    <td className="sf-td">
                      {typeof o["Amount (Rs)"] === "number"
                        ? inr(o["Amount (Rs)"])
                        : o["Amount (Rs)"]}
                    </td>
                    <td className="sf-td">{o.Time}</td>
                    <td className="sf-td">
                      <StatusBadge status={o.Status} />
                    </td>
                  </tr>
                ))}

                {(dailyData.orders || []).length === 0 && (
                  <tr>
                    <td className="sf-td text-slate-400" colSpan={6}>
                      {t("noOrdersDay")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
